import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const SectorsView = ({ sectors }) => {
  const { t } = useTranslation();

  return (
    <>
      <TableContainer>
        <Table size="medium">
          <TableHead>
            <TableRow
              sx={{
                bgcolor: '#fafafa',
                borderBottom: '2px solid #e2e8f0'
              }}
            >
              <TableCell align="center" sx={{ fontWeight: 600, color: '#475569', width: 80, fontSize: '0.8rem', py: 2 }}>
                {t('vnstock.stocks.sectorTable.ranking')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.8rem', py: 2 }}>
                {t('vnstock.stocks.valuationSector')}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.8rem', py: 2 }}>
                {t('vnstock.stocks.sectorTable.finalScore')}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.8rem', py: 2 }}>
                {t('vnstock.stocks.sectorTable.tailwind')}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.8rem', py: 2 }}>
                {t('vnstock.stocks.sectorTable.avgValScore')}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.8rem', py: 2 }}>
                {t('vnstock.stocks.sectorTable.stockCount')}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.8rem', py: 2 }}>
                {t('vnstock.stocks.pe')}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.8rem', py: 2 }}>
                {t('vnstock.stocks.pb')}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.8rem', py: 2 }}>
                {t('vnstock.stocks.sectorTable.valuationCoverage')}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.8rem', py: 2 }}>
                {t('vnstock.stocks.sectorTable.totalMarketCap')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sectors.map((sector) => (
              <TableRow
                key={sector.valuation_sector}
                hover
                sx={{
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  '&:hover': { bgcolor: alpha('#667eea', 0.03) },
                  borderBottom: '1px solid #f1f5f9'
                }}
              >
                {/* Ranking Column */}
                <TableCell align="center" sx={{ py: 2.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      color: sector.ranking <= 3 ? '#667eea' : '#64748b'
                    }}
                  >
                    {sector.ranking}
                  </Typography>
                </TableCell>

                {/* Sector Name */}
                <TableCell sx={{ py: 2.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#1e293b',
                      fontSize: '0.875rem'
                    }}
                  >
                    {sector.valuation_sector}
                  </Typography>
                </TableCell>

                {/* Final Score */}
                <TableCell align="right" sx={{ py: 2 }}>
                  <Chip
                    label={sector.final_score != null ? sector.final_score.toFixed(2) : '-'}
                    size="small"
                    sx={{
                      bgcolor: sector.final_score >= 52 ? '#c6f6d5' :
                               sector.final_score >= 50 ? '#fef3c7' : '#fed7d7',
                      color: sector.final_score >= 52 ? '#22543d' :
                             sector.final_score >= 50 ? '#78350f' : '#742a2a',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      minWidth: 60
                    }}
                  />
                </TableCell>

                {/* Tailwind Score */}
                <TableCell align="right" sx={{ py: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#4a5568' }}>
                    {sector.tailwind_score != null ? sector.tailwind_score.toFixed(2) : '-'}
                  </Typography>
                </TableCell>

                {/* Valuation Score */}
                <TableCell align="right" sx={{ py: 2 }}>
                  {sector.avg_val_score != null ? (
                    <Chip
                      label={sector.avg_val_score.toFixed(1)}
                      size="small"
                      sx={{
                        bgcolor: sector.avg_val_score >= 60 ? '#c6f6d5' :
                                 sector.avg_val_score >= 50 ? '#fefcbf' : '#fed7d7',
                        color: sector.avg_val_score >= 60 ? '#22543d' :
                               sector.avg_val_score >= 50 ? '#744210' : '#742a2a',
                        fontWeight: 700,
                        fontSize: '0.75rem'
                      }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ color: '#a0aec0' }}>-</Typography>
                  )}
                </TableCell>

                {/* Stock Count */}
                <TableCell align="right" sx={{ py: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#4a5568' }}>
                    {sector.stock_count}
                  </Typography>
                </TableCell>

                {/* P/E */}
                <TableCell align="right" sx={{ py: 2 }}>
                  <Typography variant="body2" sx={{ color: '#2d3748', fontWeight: 600 }}>
                    {sector.avg_pe != null ? sector.avg_pe.toFixed(2) : '-'}
                  </Typography>
                </TableCell>

                {/* P/B */}
                <TableCell align="right" sx={{ py: 2 }}>
                  <Typography variant="body2" sx={{ color: '#2d3748', fontWeight: 600 }}>
                    {sector.avg_pb != null ? sector.avg_pb.toFixed(2) : '-'}
                  </Typography>
                </TableCell>

                {/* Valuation Coverage */}
                <TableCell align="right" sx={{ py: 2 }}>
                  <Typography variant="body2" sx={{ color: '#2d3748', fontWeight: 600 }}>
                    {sector.valuation_coverage != null ? `${sector.valuation_coverage}%` : '-'}
                  </Typography>
                </TableCell>

                {/* Total Market Cap */}
                <TableCell align="right" sx={{ py: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#4a5568' }}>
                    {sector.total_market_cap_bn != null
                      ? `${(sector.total_market_cap_bn / 1000).toFixed(1)}T`
                      : '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default SectorsView;